"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const materials = [{
  name: "Madagascar Vanilla",
  note: "Hand-pollinated orchids from the SAVA region, cured for months to develop a deep, complex profile."
}, {
  name: "Sicilian Pistachio",
  note: "Harvested from the volcanic slopes of Mount Etna, prized for their vibrant emerald hue and intense nuttiness."
}, {
  name: "Valrhona Cacao",
  note: "Single-origin beans sourced from sustainable cooperatives, roasted to unlock notes of red fruit and spice."
}, {
  name: "Jersey Cream",
  note: "Rich, high-butterfat milk from grass-fed herds, providing the velvet foundation for every batch."
}, {
  name: "Wild Strawberry",
  note: "Foraged at peak ripeness, these berries offer a concentrated sweetness that defines our seasonal collections."
}, {
  name: "Sea Salt Flakes",
  note: "Hand-harvested from pristine coastal waters, adding a delicate, mineral crunch to our signature caramels."
}];
export default function Sourcing() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5447" id="sourcing-excellence" className="dark bg-background text-foreground py-32 md:py-44 overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-12">
          {}
          <div className="lg:col-span-8">
            <div ref={headRef} className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              {}
              <div className="flex items-center gap-3 mb-10 md:mb-14">
                <span className="font-default text-[0.6875rem] md:text-[0.8125rem] font-medium uppercase" style={{
                color: "hsl(0 100% 71%)",
                letterSpacing: "0.28em"
              }}>
                  VI
                </span>
                <span className="font-default" style={{
                color: "hsl(0 100% 71%)"
              }}>
                  —
                </span>
                <span className="font-default text-[0.6875rem] md:text-[0.8125rem] font-medium uppercase" style={{
                color: "hsl(0 100% 71%)",
                letterSpacing: "0.28em"
              }}>
                  Ingredients
                </span>
              </div>

              {}
              <h2 className="font-serif text-foreground font-light" style={{
              fontSize: "clamp(2rem, 3.4vw, 3.5rem)",
              letterSpacing: "-0.01em",
              lineHeight: "1.08"
            }}>
                Artisanal origins, <em className="italic">pure</em> ingredients
              </h2>
            </div>

            {}
            <div className="block lg:hidden mt-16 -mx-6 md:-mx-8">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/vanilla_bean_harvest.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/vanilla_bean_harvest.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/vanilla_bean_harvest.png-wvc-sizes" alt="Fresh vanilla beans harvested from the vine" loading="lazy" decoding="async" className="w-full h-[60vh] object-cover" />
            </div>

            {}
            <div className="mt-16 md:mt-20 lg:mt-24">
              {materials.map((material, i) => <MaterialRow key={i} data-index={i} index={i} name={material.name} note={material.note} />)}
            </div>
          </div>

          {}
          <div className="hidden lg:block lg:col-span-4">
            <div className="sticky top-24 h-[calc(100vh-8rem)] flex items-center">
              <div className="w-full h-[80vh] -mr-16 lg:-mr-16">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/vanilla_bean_harvest.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/vanilla_bean_harvest.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/vanilla_bean_harvest.png-wvc-sizes" alt="Fresh vanilla beans harvested from the vine" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function MaterialRow({
  index,
  name,
  note,
  ...rest
}: {
  index: number;
  name: string;
  note: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div {...rest} ref={ref} className={`group transition-all ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
    transitionDuration: "900ms",
    transitionDelay: `${index * 80}ms`
  }}>
      {}
      <div className="h-px w-20 mb-8" style={{
      backgroundColor: "hsl(0 0% 24%)"
    }} />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 pb-10 md:pb-12">
        {}
        <div className="md:col-span-7">
          <Link to="/TheArtisanalProcessPage" className="inline-block font-default text-foreground uppercase font-medium relative cursor-pointer" style={{
          fontSize: "clamp(0.75rem, 0.9vw, 0.875rem)",
          letterSpacing: "0.22em",
          lineHeight: "1.4"
        }}>
            <span className="relative inline-block">
              {name}
              <span className="absolute left-0 -bottom-1.5 h-px transition-all duration-[400ms] ease-out w-0 group-hover:w-full" style={{
              backgroundColor: "hsl(0 100% 71%)"
            }} />
            </span>
          </Link>
        </div>

        {}
        <div className="md:col-span-5">
          <p className="font-default" style={{
          color: "hsl(0 0% 70%)",
          fontSize: "clamp(0.875rem, 0.95vw, 0.9375rem)",
          letterSpacing: "0.005em",
          lineHeight: "1.7"
        }}>
            {note}
          </p>
        </div>
      </div>
    </div>;
}